<template>
  <AppLayout>
    <div class="flex flex-col items-center gap-4">
      <h1>Unclaimed Stripe Payments</h1>

      <DataTable
        :value="payments"
        :loading="loading"
        paginator
        :rows="50"
        :rowsPerPageOptions="[50, 100, 200]"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPage"
        tableStyle="min-width: 50rem"
      >
        <Column header="Created">
          <template #body="{ data }">
            {{ formatDate(data.created) }}
          </template>
        </Column>
        <Column field="email" header="Email" />
        <Column header="Amount">
          <template #body="{ data }">
            {{ data.amountTotal / 100 }} {{ data.currency.toUpperCase() }}
          </template>
        </Column>
        <Column field="credits" header="Tokens" />
      </DataTable>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import dayjs from 'dayjs';
import type { AdminUnclaimedStripePaymentDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import { useApiClient } from '@/api/apiClient';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

const { api } = useApiClient();

const payments = ref<AdminUnclaimedStripePaymentDto[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);

function formatDate(created: number): string {
  return dayjs(created).format('MMM D, YYYY HH:mm');
}

async function loadPayments() {
  loading.value = true;
  try {
    const { data, error } = await api.adminUnclaimedStripePaymentControllerList({
      query: {
        limit: String(currentLimit.value),
        offset: String(currentOffset.value),
      },
    });
    if (error) {
      throw new Error('Failed to load unclaimed stripe payments');
    }
    payments.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadPayments();
}

loadPayments();
</script>
